import React, { useState } from 'react';
import { Box, Stepper, Step, StepLabel, StepContent, Container, Paper, Button, useTheme, IconButton, Tooltip, Grow, Collapse } from '@mui/material';
import { BarChart, Hub, BatteryChargingFull, InsertChart, Assessment, FlashOn, WorkspacePremium, AccessTime, Info } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { StyledButton } from "../styles/styles";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { StyledTypography } from '../styles/styles';

const steps = [
  {
    label: 'Submit Battery Data',
    description: 'Submit the necessary battery data to begin the SoH estimation process.',
    icon: <BatteryChargingFull sx={{ color: '#e8442d' }}/>,
  },
  {
    label: 'Visualize the Data',
    description: 'Visualize the submitted data through various plots and charts for deeper insights.',
    icon: <BarChart sx={{ color: '#e8442d' }}/>,
  },
  {
    label: 'Digital Twin of your Battery',
    description: 'Generate a digital twin model of your battery for accurate SoH estimation.',
    icon: <Hub sx={{ color: '#e8442d' }}/>,
  },
  {
    label: 'Generate EIS Health Signatures',
    description: 'Create Electrochemical Impedance Spectroscopy (EIS) signatures for health analysis.',
    icon: <FlashOn sx={{ color: '#e8442d' }}/>,
  },
  {
    label: 'Battery SoH Analysis',
    description: 'Analyze the state of health of your battery using diagnostic insights and thermal runaway risk assessment.',
    icon: <Assessment sx={{ color: '#e8442d' }}/>,
    subSteps: [
      'Diagnostic Insight',
      'Thermal Runaway Risk',
    ],
  },
  {
    label: 'Battery Life-Time Analysis',
    description: 'Perform a comprehensive analysis to estimate the remaining life of the battery.',
    icon: <AccessTime sx={{ color: '#e8442d' }}/>,
  },
  {
    label: 'Battery Health Certificate',
    description: 'Generate a health certificate summarizing the battery\'s overall condition and expected performance.',
    icon: <WorkspacePremium sx={{ color: '#e8442d' }}/>,
  },
];

const BatteryScopeWorkflow = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(null);
  const navigate = useNavigate();


  const handleStepClick = (stepIndex) => {
    setActiveStep(activeStep === stepIndex ? null : stepIndex);
  };

  const handleGetStarted = () => {
    navigate('/batteryscope/submit');
  }

  return (
    <Container>
      <Paper
        elevation={0}
        style={{
          padding: theme.spacing(3),
          backgroundColor: '#000000',
          borderRadius: '1rem',
          fontFamily: 'Bai Jamjuree',
        }}
      >
        <StyledTypography
          variant="h6"
          align="center"
          gutterBottom
          color="#e8442d"
          style={{
            fontWeight: 'bold',
            fontFamily: 'Bai Jamjuree',
          }}
        >
          BatteryScope Workflow
        </StyledTypography>
        <StyledTypography
          variant="body1"
          color="textSecondary"
          style={{
            marginBottom: theme.spacing(2),
            fontFamily: 'Bai Jamjuree',
          }}
        >
          BatteryScope is a digital twin powered battery state of health (SoH) estimation tool. It uses a combination of machine learning models and physics-based models to estimate the state of health of a battery. The workflow consists of the following steps:
        </StyledTypography>
        <Stepper
          activeStep={-1}
          orientation="vertical"
          style={{
            marginTop: theme.spacing(2),
          }}
        >
          {steps.map((step, index) => (
            <Step key={index} expanded={activeStep === index}>
              <StepLabel
                icon={step.icon}
                onClick={() => handleStepClick(index)}
                style={{
                  cursor: 'pointer',
                  fontFamily: 'Bai Jamjuree',
                }}
              >
                <Box display="flex" alignItems="center">
                  <StyledTypography
                    variant="h6"
                    color="textPrimary"
                    style={{
                      fontWeight: 'bold',
                      flexGrow: 1,
                      fontFamily: 'Bai Jamjuree',
                    }}
                  >
                    {step.label}
                  </StyledTypography>
                  <Tooltip title="More Info" placement="top" arrow>
                    <IconButton size="small" sx={{ color: '#e8442d' }}>
                      <Info />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StepLabel>
              <StepContent>
                <Grow in={activeStep === index}>
                  <div>
                    <StyledTypography
                      variant="body1"
                      color="textSecondary"
                      style={{ fontFamily: 'Bai Jamjuree' }}
                    >
                      {step.description}
                    </StyledTypography>
                    {step.subSteps && (
                      <Collapse in={activeStep === index}>
                        <Box ml={4} mt={2}>
                          {step.subSteps.map((subStep, subIndex) => (
                            <StyledTypography
                              key={subIndex}
                              variant="subtitle1"
                              color="textSecondary"
                              style={{ fontFamily: 'Bai Jamjuree' }}
                            >
                              {subIndex === 0 ? 'a) ' : 'b) '}
                              {subStep}
                            </StyledTypography>
                          ))}
                        </Box>
                      </Collapse>
                    )}
                  </div>
                </Grow>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        <Box textAlign="center" mb={4}>
          <StyledButton
            onClick={handleGetStarted}
            variant="contained"
            className='gap-2'
          >
            Get Started <ArrowForwardIcon />
          </StyledButton>
        </Box>
      </Paper>
    </Container>
  );
};

export default BatteryScopeWorkflow;
